import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Dispute, EyeIcon, PencilIcon, UrgentIcon } from "../../assets";
import ToolTip from "./ToolTip";

const TableWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
    padding-bottom: 50px;
`;

const TableHead = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  border-bottom: solid 1px rgba(136, 136, 136, 0.1);
  align-self: stretch;
  box-sizing: border-box;
  padding: 8px 0px;
  flex-shrink: 0;
`;

const TableRow = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Cell = styled.div`
  display: flex;
  justify-content: ${(props) => props.align || "flex-start"};
  flex-direction: row;
  align-items: center;
  flex: ${(props) => (props.flex ? "1" : "none")};
  gap: 8px;
  width: ${(props) => props.width || "auto"};
  box-sizing: border-box;

  /* Prevent overflow */
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;

  /* Ensure text doesn't spill out */
  min-width: 0;
`;

const Text = styled.span`
  color: ${(props) => props.color || "rgb(33, 37, 67)"};
  text-overflow: ellipsis;
  font-size: ${(props) => props.fontSize || "16px"};
  font-family: Montserrat, sans-serif;
  font-weight: ${(props) => props.fontWeight || "500"};
  line-height: 160%;
  text-align: left;
  /* Prevent text overflow */
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const STATUS_COLORS = {
  won: "rgba(18, 124, 78, 0.1)",
  "verbally committed": "rgba(244, 186, 52, 0.1)",
  "proposal sent": "rgba(244, 186, 52, 0.1)",
  lost: "rgba(240, 70, 63, 0.1)",
  "lead identified": "rgba(89, 84, 222, 0.1)",
  opportunity: "rgba(89, 84, 222, 0.1)",
};

const StatusTag = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  align-self: stretch;
  background: ${(props) => {
    const normalizedStatus = props.status?.toLowerCase?.();
    const color = STATUS_COLORS[normalizedStatus];
    return color
      ? `linear-gradient(-90deg, ${color}, ${color})`
      : "transparent";
  }};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 4px 8px;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  color: ${(props) => {
    const normalizedStatus = props.status?.toLowerCase?.();

    switch (normalizedStatus) {
      case "lead identified":
        return "rgba(89, 84, 222, 1)";
      case "proposal sent":
      case "verbally committed":
        return "rgba(244, 186, 52, 1)";
      case "lost":
        return "rgb(240, 70, 63)";
      case "won":
        return "rgba(18, 124, 78, 1)";
      default:
        return "inherit";
    }
  }};
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;
const Viewdetails = styled.span`
  font-family: Montserrat;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0%;
  color: rgba(97, 98, 197, 1);
`;
const GFeildsInfo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: cover;
  cursor: pointer;
`;
const Table = ({
  headers,
  data,
  columns,
  viewClick,
  editClick,
  deleteClick,
  handleEditClick,
  isLoading,
}) => {
  const navigate = useNavigate(); //

  const handleRowClick = (item) => {
    navigate(`/referral/${item._id}`);
  };
  const renderCell = (column, item) => {
    const value = item[column.key];

    if (column.type === "status") {
      const isEditable = value !== "Lost" && value !== "Won";
      return (
        <div style={{ display: "flex", gap: "12px" }}>
          <StatusTag status={value}>
            <StatusText status={value}>{value}</StatusText>
          </StatusTag>
          {isEditable && (
            <>
              <img
                src={PencilIcon}
                alt="Edit"
                style={{ cursor: "pointer", width: 14 }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click
                  handleEditClick(item);
                }}
              />
            </>
          )}
        </div>
      );
    }

    if (column.key === "action") {
      return (
        <img
          src={EyeIcon}
          alt="View Details"
          onClick={() => handleRowClick(item)}
        />
      );
    }

    const isDate =
      typeof value === "string" &&
      (column.key.toLowerCase().includes("date") || !isNaN(Date.parse(value)));

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    if (isDate) {
      const status = item.referral_status;
      const isClosed = status === "Won" || status === "Lost";
      let dateValue;
      if (isClosed) {
        console.log("isClosed", item.close_date);

        dateValue = item.close_date;
      } else {
        dateValue = item.expected_close_date;
      }
      if (!dateValue) return null;

      const displayValue = formatDate(dateValue);
      const isPastDate =
        !isClosed && new Date(dateValue) < new Date(new Date().toDateString());

      return (
        <Text
          color={isPastDate ? "red" : column.color}
          fontSize={column.fontSize}
          fontWeight={column.fontWeight}
        >
          {displayValue}
        </Text>
      );
    }

    const displayValue = isDate ? formatDate(value) : value ?? "-";

    if (column.key === "referrer_id") {
      return (
        <Text
          color={column.color}
          fontSize={column.fontSize}
          fontWeight={column.fontWeight}
          style={{
            display: "flex",
            direction: "row",
            gap: "4px",
            paddingRight: "4px",
          }}
        >
          {item[column.key]}
          {item.dispute && item.dispute.dispute_status === "Open" && (
            <TooltipWrapper>
              <GFeildsInfo src={Dispute} alt="icon" />
              <TooltipText>This referral has a dispute.</TooltipText>
            </TooltipWrapper>
          )}
          {item.is_urgent && (
            <TooltipWrapper>
              <GFeildsInfo src={UrgentIcon} alt="icon" />
              <TooltipText>
                Urgent: time-sensitive referral. Quick response requested.
              </TooltipText>
            </TooltipWrapper>
          )}
        </Text>
      );
    }

    return (
      <Text
        color={column.color}
        fontSize={column.fontSize}
        fontWeight={column.fontWeight}
      >
        {displayValue}
      </Text>
    );
  };

  return (
    <TableWrapper>
      <TableHead>
        {headers.map((header, index) => (
          <Cell
            key={index}
            width={header.width}
            flex={header.flex}
            align={header.align}
          >
            {header.label === "Expected/Actual Close Date" ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <Text color="rgb(100, 116, 139)">{header.label}</Text>
                <ToolTip message="If referral is won/lost this shows the actual close date. Otherwise it is the expected close date." />
              </div>
            ) : (
              <Text color="rgb(100, 116, 139)">{header.label}</Text>
            )}
          </Cell>
        ))}
      </TableHead>
      {!data || data.length === 0 ? (
        <TableRow>
          <Cell colSpan={columns.length}>
            <Text>{isLoading ? "Loading..." : "No data found"}</Text>
          </Cell>
        </TableRow>
      ) : (
        data.map((item, rowIndex) => (
          <TableRow
            key={rowIndex}
            // onClick={() => handleRowClick(item)}
            style={{ cursor: "pointer" }}
          >
            {columns.map((column, colIndex) => (
              <Cell
                key={colIndex}
                width={column.width}
                flex={column.flex}
                align={column.align}
              >
                {renderCell(column, item)}
              </Cell>
            ))}
          </TableRow>
        ))
      )}
    </TableWrapper>
  );
};

export default Table;


const TooltipText = styled.div`
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s;
  color: #000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  max-width: 280px;
  width: 200px;
  background: #ffffff;
  backdrop-filter: blur(8px);
  font-family: "Montserrat", sans-serif;

  &::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent #ffffff transparent;
  }
`;
const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  padding: 1px;

  &:hover ${TooltipText} {
    visibility: visible;
    opacity: 1;
  }
`;
